/**
 * CURRENCY PICKER
 *
 * Per-item currency selector used next to Purchase Price in every
 * Add/Detail modal. Renders the full SUPPORTED_CURRENCIES list with the
 * user's pinned favorites floated to the top. Star icon on each row
 * pins/unpins; the pin list persists on the user profile so it syncs
 * across devices.
 *
 * Distinct from the SELLER currency (profile.currency), which controls
 * destash-listing display for buyers on Marketplace. This picker only
 * touches the item's own `priceCurrency` — a record-keeping field for
 * "what did I pay in?"
 */

import React, { useEffect, useRef, useState } from 'react';
import { Star, ChevronDown } from 'lucide-react';
import {
  SUPPORTED_CURRENCIES,
  getCurrencyInfo,
  type CurrencyInfo,
} from '../../constants/currencies';

interface CurrencyPickerProps {
  /** Currently selected ISO code. Falls back to sellerDefault when absent. */
  value: string | undefined | null;
  /** Fires when the user selects a currency. */
  onChange: (code: string) => void;
  /** ISO codes the user has pinned. May be undefined for legacy profiles. */
  pinnedCurrencies?: string[];
  /** Fires when the user pins/unpins a currency in the dropdown. Caller
   *  is responsible for persisting to the user profile. */
  onPinChange?: (next: string[]) => void;
  /** Fallback currency when `value` is unset — usually the seller's
   *  profile currency so a legacy item shows the user's own default
   *  instead of an ambiguous placeholder. */
  sellerDefault?: string;
  disabled?: boolean;
  className?: string;
}

const CurrencyPicker: React.FC<CurrencyPickerProps> = ({
  value,
  onChange,
  pinnedCurrencies = [],
  onPinChange,
  sellerDefault = 'USD',
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const effectiveCode = value || sellerDefault;
  const current = getCurrencyInfo(effectiveCode);

  // Sort: pinned currencies (in the order the user pinned them) first,
  // then the remainder of SUPPORTED_CURRENCIES in its declared order.
  const pinnedSet = new Set(pinnedCurrencies);
  const pinnedList: CurrencyInfo[] = pinnedCurrencies
    .map((code) => SUPPORTED_CURRENCIES.find((c) => c.code === code))
    .filter((c): c is CurrencyInfo => !!c);
  const restList: CurrencyInfo[] = SUPPORTED_CURRENCIES.filter(
    (c) => !pinnedSet.has(c.code),
  );

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const togglePin = (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onPinChange) return;
    const next = pinnedSet.has(code)
      ? pinnedCurrencies.filter((c) => c !== code)
      : [...pinnedCurrencies, code];
    onPinChange(next);
  };

  const select = (code: string) => {
    onChange(code);
    setIsOpen(false);
  };

  const renderRow = (info: CurrencyInfo) => {
    const isPinned = pinnedSet.has(info.code);
    const isSelected = info.code === effectiveCode;
    return (
      <button
        key={info.code}
        type="button"
        onClick={() => select(info.code)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-default ${
          isSelected ? 'bg-purple-50' : ''
        }`}
      >
        {onPinChange && (
          <button
            type="button"
            onClick={(e) => togglePin(info.code, e)}
            className={`p-1 rounded hover:bg-gray-200 ${
              isPinned ? 'text-yellow-500' : 'text-gray-300'
            }`}
            aria-label={isPinned ? `Unpin ${info.label}` : `Pin ${info.label}`}
          >
            <Star
              className="w-4 h-4"
              fill={isPinned ? 'currentColor' : 'none'}
            />
          </button>
        )}
        <span className="font-mono text-sm text-gray-500 w-8 text-right">
          {info.code}
        </span>
        <span className="text-sm text-gray-900 flex-1">{info.label}</span>
        <span className="text-sm text-gray-500">{info.symbol}</span>
      </button>
    );
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((v) => !v)}
        disabled={disabled}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-2">
          <span className="font-mono text-gray-500">{current.code}</span>
          <span className="text-gray-900">{current.symbol}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 w-72 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg right-0"
        >
          {pinnedList.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide font-bold text-gray-500 bg-gray-50">
                Pinned
              </div>
              {pinnedList.map(renderRow)}
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide font-bold text-gray-500 bg-gray-50">
                All currencies
              </div>
            </>
          )}
          {restList.map(renderRow)}
        </div>
      )}
    </div>
  );
};

export default CurrencyPicker;
